import React, { useState, useEffect } from "react";
import { Typography, Box, Tab, Tabs } from '@mui/material';
import axios from "axios";
import GeneralInformationBox from "../../Utilities/NCProForms/GeneralInformationBox";
import AffectedItems from "../../Utilities/NCProForms/AffectedItems";
import NonConformanceDescription from "../../Utilities/NCProForms/NonConformanceDescription";
import AdditionalInformation from "../../Utilities/NCProForms/AdditionalInformation";
import { useLocation, useNavigate } from 'react-router-dom';
import CircularLoader from "../../Common/CircularLoader";
import FishBonePage from "./RcAnalysis/FishBoneDiagram";
import WhysFlowPage from "./RcAnalysis/WhyAnalysis";
import ProgressStepper from "../../Utilities/ProgressStepper";
import { getHeaders } from "../../../Helpers/NCRHelpers";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { setTab } from "../../../Helpers/helperSlice";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const defaultFishbone = {
  manpower: Array(3).fill().map((_, i) => ({ data: '', id: `${i + 1}`, is_highlighted: false })),
  method: Array(3).fill().map((_, i) => ({ data: '', id: `${i + 1}`, is_highlighted: false })),
  machine: Array(3).fill().map((_, i) => ({ data: '', id: `${i + 1}`, is_highlighted: false })),
  material: Array(3).fill().map((_, i) => ({ data: '', id: `${i + 1}`, is_highlighted: false })),
  environment: Array(3).fill().map((_, i) => ({ data: '', id: `${i + 1}`, is_highlighted: false })),
  measurement: Array(3).fill().map((_, i) => ({ data: '', id: `${i + 1}`, is_highlighted: false }))
};

const initialWhyData = [
  {
    id: 1,
    title: 'Why 1',
    content: '',
    color: '#FFD700',
    is_submitted: false,
  },
];

export default function AnalysisDetailsComponent({ content }) {
  const [inSideTabValue, setInSideTabValue] = useState(0);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const { tab } = useSelector((state) => state.helper);
  const location = useLocation();
  const initialTab = location.state?.fromAction ? tab : 0;
  const [tabValue, setTabValue] = useState(initialTab);
  const [isEditBtn, setIsEditBtn] = useState(false);
  const [isWhyEditBtn, setIsWhyEditBtn] = useState(false);

  const actionFormData = {
    implement_interim_containment_action: '',
    implement_interim_containment_action_other: '',
    escape_point: '',
    ndrc_identification: '',
    corrective_actions_ndrc: "",
    process_root_cause_description: "",
    process_root_cause_identification: "",
    corrective_actions_process_root_cause: "",
    system_root_cause_identification: "",
    corrective_actions_system_root_cause: "",
    preventive_action_plan: null,
    preventive_action_implementation_timeline: null,
    action_attachments: "",
    action_attachments_file_name: "",
  }
  const [formData, setFormData] = useState(actionFormData);
  const [fishBoneForm, setFishBoneForm] = useState(defaultFishbone);
  const [prevFishBoneForm, setPrevFishBoneForm] = useState(defaultFishbone);
  const [whyData, setWhyData] = useState(initialWhyData);
  const [preWhyData, setPreWhyData] = useState(initialWhyData);
  const [current, setCurrent] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const mergeFishBoneData = (prevForm, fishBoneData) => {
    const updatedForm = structuredClone(prevForm);

    Object.keys(fishBoneData || {}).forEach(key => {
      if (Array.isArray(updatedForm[key]) && Array.isArray(fishBoneData[key])) {
        updatedForm[key] = updatedForm[key].map(item => {
          const updatedItem = fishBoneData[key]?.find(resItem => resItem?.id === item?.id);
          return updatedItem ? { ...item, ...updatedItem } : item;
        });
      }
    });

    return updatedForm;
  };

  const fetchFishboneData = async (ncrId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/fishbone/${ncrId}`, getHeaders());
      return response?.data?.data || [];
    } catch (error) {
      toast.error(error?.message || 'Error fetching Why Why data');
      return [];
    }
  };

  const getFishboneData = async (ncrId) => {
    const result = await fetchFishboneData(ncrId);

    if (result) {
      const mergedData = mergeFishBoneData(structuredClone(defaultFishbone), result);
      const findEditStatus = Object.values(mergedData).some(items => items.some(item => item.is_highlighted))
      setIsEditBtn(findEditStatus);
      setFishBoneForm(mergedData);
      setPrevFishBoneForm(structuredClone(mergedData));
    }
  };

  const fetchWhyWhyData = async (ncrId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/whywhy/${ncrId}`, getHeaders());
      return response?.data?.data || [];
    } catch (error) {
      toast.error(error?.message || 'Error fetching Why Why data');
      return [];
    }
  };

  const getWhyAnalysisData = async (ncrId) => {
    const result = await fetchWhyWhyData(ncrId);

    if (result?.length > 0) {
      setWhyData(structuredClone(result));
      setPreWhyData(structuredClone(result));
      setCurrent(result?.length - 1);
      setIsWhyEditBtn(result?.some(item => item?.is_submitted));
    }
  };

  const getFromData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/ncr/forms/${content.ncrId}`, getHeaders());
      setFormData(response?.data?.ncrForm);

      getWhyAnalysisData(response?.data?.ncrForm?.ncr_id);
      getFishboneData(response?.data?.ncrForm?.ncr_id);
    } catch (error) {
      console.error('Error fetching ncr:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getFromData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.state?.fromAction) {
      dispatch(setTab(0));
    }
  }, [location.state?.fromAction, dispatch]);

  return (
    <>
      {isLoading ? (
        <CircularLoader label="NCRDetails" />
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 14 }}>
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ pr: '4px' }}>
                <img
                  src="/images/leftArrow.png"
                  alt="Icon"
                  style={{ width: 24, height: 24, marginRight: 4, cursor: 'pointer' }}
                  onClick={() => {
                    if (location.state?.isRefreshValue === 'analysis') {
                      navigate('/ncpro/analysis')
                    } else {
                      dispatch(setTab(1))
                      navigate(`/ncpro/action/${formData.ncr_id}`, {
                        state: { fromRC: true, ncr_id: formData?.ncr_id }
                      })
                    }
                  }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  {formData.ncr_id} - RC Analysis
                </Typography>
              </Box>
            </Box>
            <Box>
              <ProgressStepper status={['completed', 'inProgress', 'notStarted', 'notStarted']} />
            </Box>
          </Box>
          <Box
            sx={{
              height: '80vh',
              overflow: 'auto',
              border: '1px solid #ccc',
              borderRadius: '8px',
              backgroundColor: '#fff',
              marginTop: '16px',
              '&::-webkit-scrollbar': {
                width: '7px',
                height: '7px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(211, 211, 211, 1)',
                borderRadius: '5px',
              }
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Box sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                zIndex: 10,
                borderBottom: 1,
                borderColor: 'divider',
              }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
                  <Tab
                    label="General information"
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '14px',
                      fontWeight: tabValue === 0 ? '700' : '400',
                      color: tabValue === 0 ? 'rgba(42, 61, 189, 1) !Important' : 'rgba(0, 0, 0, 1) !Important',
                      lineHeight: '16.8px',
                      textTransform: 'none',
                      py: "20px"
                    }}
                  />
                  <Tab
                    label={'Root cause Analysis'}
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '14px',
                      fontWeight: tabValue === 1 ? '700' : '400',
                      color: tabValue === 1 ? 'rgba(42, 61, 189, 1) !Important' : 'rgba(0, 0, 0, 1) !Important',
                      lineHeight: '16.8px',
                      textTransform: 'none',
                      py: "20px"
                    }}
                  />
                </Tabs>
              </Box>
              <CustomTabPanel value={tabValue} index={0}>
                <GeneralInformationBox content={{ formData: formData, inputDisable: true }} />
                <AffectedItems content={{ formData: formData, inputDisable: true }} />
                <NonConformanceDescription content={{ formData: formData, inputDisable: true }} />
                <AdditionalInformation content={{ label: "action", formData: formData, inputDisable: true }} />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={1}>
                <Box sx={{
                  position: 'relative',
                  margin: "40px",
                  border: "2px solid rgba(128, 128, 128, 0.2)",
                  borderRadius: "8px",
                }}>
                  <Box sx={{
                    position: 'sticky',
                    top: "58px",
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    zIndex: 5,
                  }} className='fishBoneClass'>
                    <Tabs
                      value={inSideTabValue}
                      onChange={(e, value) => {
                        // Reset edit states based on data when switching tabs
                        const hasFishboneData = Object.values(fishBoneForm).some(items => items.some(item => item.is_highlighted));
                        setIsEditBtn(hasFishboneData);
                        const hasWhyData = whyData?.some(item => item?.is_submitted);
                        setIsWhyEditBtn(hasWhyData);
                        setInSideTabValue(value);
                      }}
                      aria-label="basic tabs example"
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: "space-around",
                        borderBottom: '2px solid rgba(128, 128, 128, 0.2)',
                      }}
                      TabIndicatorProps={{
                        style: {
                          height: 3,
                          backgroundColor: '#2A3DBD',
                        },
                      }}
                    >
                      <Tab
                        label="Fish-bone Analysis"
                        sx={{
                          flex: 1,
                          fontFamily: 'Lato, sans-serif',
                          fontSize: '14px',
                          fontWeight: inSideTabValue === 0 ? '700' : '400',
                          color: inSideTabValue === 0 ? 'rgba(42, 61, 189, 1)' : 'rgba(0, 0, 0, 1)',
                          lineHeight: '16.8px',
                          textTransform: 'none',
                          py: '20px'
                        }}
                      />
                      <Tab
                        label="Why-Why Analysis"
                        sx={{
                          flex: 1, // this ensures 50% width
                          fontFamily: 'Lato, sans-serif',
                          fontSize: '14px',
                          fontWeight: inSideTabValue === 1 ? '700' : '400',
                          color: inSideTabValue === 1 ? 'rgba(42, 61, 189, 1)' : 'rgba(0, 0, 0, 1)',
                          lineHeight: '16.8px',
                          textTransform: 'none',
                          py: '20px',
                        }}
                      />
                    </Tabs>
                  </Box>
                  <CustomTabPanel value={inSideTabValue} index={0}>
                    <FishBonePage
                      fishboneState={{
                        formData,
                        defaultFishbone,
                        fishBoneForm,
                        setFishBoneForm,
                        prevFishBoneForm,
                        setPrevFishBoneForm,
                        isEditBtn,
                        setIsEditBtn,
                      }}
                    />
                  </CustomTabPanel>

                  <CustomTabPanel value={inSideTabValue} index={1}>
                    <WhysFlowPage
                      whyAnalysisState={{
                        formData,
                        whyData,
                        setWhyData,
                        preWhyData,
                        setPreWhyData,
                        current,
                        setCurrent,
                        isWhyEditBtn,
                        setIsWhyEditBtn
                      }}
                    />
                  </CustomTabPanel>
                </Box>
              </CustomTabPanel>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
